import { Fragment, useState, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { graphqlClient } from "@/lib/graphql";
import { gql } from "graphql-request";
import { Link, useSearchParams } from "react-router-dom";
import { Pagination } from "@/components/ui/Pagination";
import { Badge } from "@/components/ui/Badge";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { FilterPopover } from "@/components/ui/FilterPopover";
import { RadioFilter } from "@/components/ui/filters/RadioFilter";
import { CheckboxFilter } from "@/components/ui/filters/CheckboxFilter";
import {
  DateRangeFilter,
  DATE_PRESETS,
  resolveDatePreset,
  type DatePreset,
} from "@/components/ui/filters/DateRangeFilter";

interface AdminShopListing {
  id: string;
  shopName: string;
  listingName: string | null;
  listingUrl: string;
  price: number | null;
  priceUpdatedAt: string | null;
  inStock: boolean | null;
  stockCheckedAt: string | null;
  createdAt: string;
  updatedAt: string;
  gameId: string | null;
  gameTitle: string | null;
  gameListingsCount: number;
  variation: string | null;
}

interface AdminShopListingsResult {
  shopListings: AdminShopListing[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  shopNames: string[];
}

const ADMIN_SHOP_LISTINGS_QUERY = gql`
  query AdminShopListings(
    $page: Int
    $perPage: Int
    $query: String
    $gameQuery: String
    $shopName: String
    $inStock: String
    $sortBy: String
    $sortDir: String
    $priceUpdatedFrom: String
    $priceUpdatedTo: String
    $stockCheckedFrom: String
    $stockCheckedTo: String
    $createdFrom: String
    $createdTo: String
    $groupByGame: Boolean
  ) {
    adminShopListings(
      page: $page
      perPage: $perPage
      query: $query
      gameQuery: $gameQuery
      shopName: $shopName
      inStock: $inStock
      sortBy: $sortBy
      sortDir: $sortDir
      priceUpdatedFrom: $priceUpdatedFrom
      priceUpdatedTo: $priceUpdatedTo
      stockCheckedFrom: $stockCheckedFrom
      stockCheckedTo: $stockCheckedTo
      createdFrom: $createdFrom
      createdTo: $createdTo
      groupByGame: $groupByGame
    ) {
      shopListings {
        id
        shopName
        listingName
        listingUrl
        price
        priceUpdatedAt
        inStock
        stockCheckedAt
        createdAt
        updatedAt
        gameId
        gameTitle
        gameListingsCount
        variation
      }
      totalCount
      totalPages
      currentPage
      shopNames
    }
  }
`;

type SortField =
  | "shop_name"
  | "listing_name"
  | "price"
  | "price_updated_at"
  | "stock_checked_at"
  | "created_at"
  | "updated_at"
  | "listings_count";

const STOCK_OPTIONS = [
  { value: "", label: "Visi" },
  { value: "true", label: "Sandėlyje" },
  { value: "false", label: "Išparduota" },
  { value: "unknown", label: "Nežinoma" },
];

function FunnelIcon({ active }: { active: boolean }) {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none">
      {active ? (
        <path d="M2 3h12l-4.5 5.5v4l-3 1.5V8.5L2 3z" fill="currentColor" />
      ) : (
        <path
          d="M2 3h12l-4.5 5.5v4l-3 1.5V8.5L2 3z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

type FilterColumn =
  | "shop"
  | "stock"
  | "priceUpdated"
  | "stockChecked"
  | "created"
  | null;

function stockBadge(inStock: boolean | null) {
  if (inStock === true) return <Badge variant="success">Sandėlyje</Badge>;
  if (inStock === false) return <Badge variant="danger">Išparduota</Badge>;
  return <Badge variant="default">Nežinoma</Badge>;
}

export function AdminShopListingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openFilter, setOpenFilter] = useState<FilterColumn>(null);
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");
  const [gameInput, setGameInput] = useState(searchParams.get("game") || "");

  const shopRef = useRef<HTMLTableCellElement>(null);
  const stockRef = useRef<HTMLTableCellElement>(null);
  const priceUpdatedRef = useRef<HTMLTableCellElement>(null);
  const stockCheckedRef = useRef<HTMLTableCellElement>(null);
  const createdRef = useRef<HTMLTableCellElement>(null);

  const page = Number(searchParams.get("page")) || 1;
  const sortBy = (searchParams.get("sortBy") as SortField) || "updated_at";
  const sortDir = (searchParams.get("sortDir") as "asc" | "desc") || "desc";
  const searchQuery = searchParams.get("q") || "";
  const gameQuery = searchParams.get("game") || "";
  const shopFilterRaw = searchParams.get("shop") || "";
  const shopFilters = shopFilterRaw ? shopFilterRaw.split(",") : [];
  const stockFilter = searchParams.get("stock") || "";

  const priceUpdatedPreset =
    (searchParams.get("priceUpdatedPreset") as DatePreset) || null;
  const priceUpdatedFrom = searchParams.get("priceUpdatedFrom") || "";
  const priceUpdatedTo = searchParams.get("priceUpdatedTo") || "";
  const stockCheckedPreset =
    (searchParams.get("stockCheckedPreset") as DatePreset) || null;
  const stockCheckedFrom = searchParams.get("stockCheckedFrom") || "";
  const stockCheckedTo = searchParams.get("stockCheckedTo") || "";
  const createdPreset = (searchParams.get("createdPreset") as DatePreset) || null;
  const createdFrom = searchParams.get("createdFrom") || "";
  const createdTo = searchParams.get("createdTo") || "";
  const groupByGame = searchParams.get("groupByGame") === "1";

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value) {
              next.set(key, value);
            } else {
              next.delete(key);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSort = (field: SortField) => {
    const newDir =
      sortBy === field
        ? sortDir === "asc"
          ? "desc"
          : "asc"
        : field === "shop_name" || field === "listing_name"
          ? "asc"
          : "desc";
    updateParams({ sortBy: field, sortDir: newDir, page: "" });
  };

  const handlePageChange = (newPage: number) => {
    updateParams({ page: newPage > 1 ? String(newPage) : "" });
  };

  const toggleFilter = (col: FilterColumn) => {
    setOpenFilter((prev) => (prev === col ? null : col));
  };

  const resolvedPriceUpdated = priceUpdatedPreset
    ? resolveDatePreset(priceUpdatedPreset)
    : { from: priceUpdatedFrom, to: priceUpdatedTo };
  const resolvedStockChecked = stockCheckedPreset
    ? resolveDatePreset(stockCheckedPreset)
    : { from: stockCheckedFrom, to: stockCheckedTo };
  const resolvedCreated = createdPreset
    ? resolveDatePreset(createdPreset)
    : { from: createdFrom, to: createdTo };

  const { data, isLoading } = useQuery({
    queryKey: [
      "adminShopListings",
      page,
      searchQuery,
      gameQuery,
      sortBy,
      sortDir,
      shopFilterRaw,
      stockFilter,
      resolvedPriceUpdated.from,
      resolvedPriceUpdated.to,
      resolvedStockChecked.from,
      resolvedStockChecked.to,
      resolvedCreated.from,
      resolvedCreated.to,
      groupByGame,
    ],
    queryFn: () =>
      graphqlClient.request<{ adminShopListings: AdminShopListingsResult }>(
        ADMIN_SHOP_LISTINGS_QUERY,
        {
          page,
          perPage: 50,
          sortBy,
          sortDir,
          query: searchQuery || undefined,
          gameQuery: gameQuery || undefined,
          shopName: shopFilterRaw || undefined,
          inStock: stockFilter || undefined,
          priceUpdatedFrom: resolvedPriceUpdated.from || undefined,
          priceUpdatedTo: resolvedPriceUpdated.to || undefined,
          stockCheckedFrom: resolvedStockChecked.from || undefined,
          stockCheckedTo: resolvedStockChecked.to || undefined,
          createdFrom: resolvedCreated.from || undefined,
          createdTo: resolvedCreated.to || undefined,
          groupByGame: groupByGame || undefined,
        },
      ),
  });

  const result = data?.adminShopListings;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleString("lt-LT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const shopOptions = (result?.shopNames ?? []).map((s) => ({
    value: s,
    label: s,
  }));

  const isFilterActive = (col: FilterColumn) => {
    switch (col) {
      case "shop":
        return shopFilters.length > 0;
      case "stock":
        return stockFilter !== "";
      case "priceUpdated":
        return (
          priceUpdatedPreset !== null ||
          priceUpdatedFrom !== "" ||
          priceUpdatedTo !== ""
        );
      case "stockChecked":
        return (
          stockCheckedPreset !== null ||
          stockCheckedFrom !== "" ||
          stockCheckedTo !== ""
        );
      case "created":
        return createdPreset !== null || createdFrom !== "" || createdTo !== "";
      default:
        return false;
    }
  };

  const getActiveFilters = () => {
    const filters: { label: string; onRemove: () => void }[] = [];

    if (gameQuery) {
      filters.push({
        label: `Žaidimas: ${gameQuery}`,
        onRemove: () => {
          setGameInput("");
          updateParams({ game: "", page: "" });
        },
      });
    }
    if (shopFilters.length > 0) {
      filters.push({
        label: `Parduotuvė: ${shopFilters.join(", ")}`,
        onRemove: () => updateParams({ shop: "", page: "" }),
      });
    }
    if (stockFilter) {
      const label =
        STOCK_OPTIONS.find((o) => o.value === stockFilter)?.label || stockFilter;
      filters.push({
        label: `Sandėlis: ${label}`,
        onRemove: () => updateParams({ stock: "", page: "" }),
      });
    }
    if (priceUpdatedPreset) {
      const label =
        DATE_PRESETS.find((p) => p.value === priceUpdatedPreset)?.label ||
        priceUpdatedPreset;
      filters.push({
        label: `Kaina atnaujinta: ${label}`,
        onRemove: () =>
          updateParams({
            priceUpdatedPreset: "",
            priceUpdatedFrom: "",
            priceUpdatedTo: "",
            page: "",
          }),
      });
    } else if (priceUpdatedFrom || priceUpdatedTo) {
      const parts = [priceUpdatedFrom, priceUpdatedTo].filter(Boolean).join(" – ");
      filters.push({
        label: `Kaina atnaujinta: ${parts}`,
        onRemove: () =>
          updateParams({ priceUpdatedFrom: "", priceUpdatedTo: "", page: "" }),
      });
    }
    if (stockCheckedPreset) {
      const label =
        DATE_PRESETS.find((p) => p.value === stockCheckedPreset)?.label ||
        stockCheckedPreset;
      filters.push({
        label: `Sandėlis tikrintas: ${label}`,
        onRemove: () =>
          updateParams({
            stockCheckedPreset: "",
            stockCheckedFrom: "",
            stockCheckedTo: "",
            page: "",
          }),
      });
    } else if (stockCheckedFrom || stockCheckedTo) {
      const parts = [stockCheckedFrom, stockCheckedTo].filter(Boolean).join(" – ");
      filters.push({
        label: `Sandėlis tikrintas: ${parts}`,
        onRemove: () =>
          updateParams({ stockCheckedFrom: "", stockCheckedTo: "", page: "" }),
      });
    }
    if (createdPreset) {
      const label =
        DATE_PRESETS.find((p) => p.value === createdPreset)?.label || createdPreset;
      filters.push({
        label: `Sukurtas: ${label}`,
        onRemove: () =>
          updateParams({
            createdPreset: "",
            createdFrom: "",
            createdTo: "",
            page: "",
          }),
      });
    } else if (createdFrom || createdTo) {
      const parts = [createdFrom, createdTo].filter(Boolean).join(" – ");
      filters.push({
        label: `Sukurtas: ${parts}`,
        onRemove: () =>
          updateParams({ createdFrom: "", createdTo: "", page: "" }),
      });
    }

    return filters;
  };

  const activeFilters = getActiveFilters();

  const clearAllFilters = () => {
    setSearchInput("");
    setGameInput("");
    updateParams({
      q: "",
      game: "",
      shop: "",
      stock: "",
      priceUpdatedPreset: "",
      priceUpdatedFrom: "",
      priceUpdatedTo: "",
      stockCheckedPreset: "",
      stockCheckedFrom: "",
      stockCheckedTo: "",
      createdPreset: "",
      createdFrom: "",
      createdTo: "",
      page: "",
    });
  };

  const HeaderCell = ({
    field,
    label,
    filterCol,
    cellRef,
    children,
  }: {
    field?: SortField;
    label: string;
    filterCol: FilterColumn;
    cellRef: React.RefObject<HTMLTableCellElement | null>;
    children?: React.ReactNode;
  }) => {
    const active = isFilterActive(filterCol);

    return (
      <th
        ref={cellRef}
        className="relative px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
      >
        <div className="inline-flex items-center gap-1.5">
          {field ? (
            <button
              className="inline-flex items-center gap-1 hover:text-gray-700"
              onClick={() => handleSort(field)}
            >
              {label}
              {sortBy === field && (
                <span className="text-primary-800">
                  {sortDir === "asc" ? "↑" : "↓"}
                </span>
              )}
            </button>
          ) : (
            <span>{label}</span>
          )}
          {children && (
            <button
              className={`rounded p-0.5 transition-colors ${
                active
                  ? "text-primary-800 hover:text-primary-600"
                  : "text-gray-300 hover:text-gray-500"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                toggleFilter(filterCol);
              }}
            >
              <FunnelIcon active={active} />
            </button>
          )}
        </div>
        {children && (
          <FilterPopover
            open={openFilter === filterCol}
            onClose={() => setOpenFilter(null)}
            anchorRef={cellRef}
          >
            {children}
          </FilterPopover>
        )}
      </th>
    );
  };

  const SortOnlyHeader = ({
    field,
    label,
  }: {
    field: SortField;
    label: string;
  }) => (
    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
      <button
        className="inline-flex items-center gap-1 hover:text-gray-700"
        onClick={() => handleSort(field)}
      >
        {label}
        {sortBy === field && (
          <span className="text-primary-800">
            {sortDir === "asc" ? "↑" : "↓"}
          </span>
        )}
      </button>
    </th>
  );

  return (
    <div className="px-4 py-8">
      <Link
        to="/admin"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        ← Admin valdymo skydas
      </Link>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Parduotuvių skelbimai
          </h1>
          {result && (
            <p className="mt-1 text-sm text-gray-500">
              Iš viso: {result.totalCount}
            </p>
          )}
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {activeFilters.map((filter) => (
            <span
              key={filter.label}
              className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-sm text-primary-800"
            >
              {filter.label}
              <button
                onClick={filter.onRemove}
                className="ml-0.5 rounded-full p-0.5 hover:bg-primary-100"
              >
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
          {activeFilters.length > 1 && (
            <button
              onClick={clearAllFilters}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Išvalyti visus
            </button>
          )}
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateParams({ q: searchInput, page: "" });
          }}
          className="flex gap-2"
        >
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Ieškoti pagal žaidimą, parduotuvę ar pavadinimą..."
            className="w-80 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <button
            type="submit"
            className="rounded-lg bg-primary-800 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Ieškoti
          </button>
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                updateParams({ q: "", page: "" });
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Išvalyti
            </button>
          )}
        </form>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateParams({ game: gameInput, page: "" });
          }}
          className="flex gap-2"
        >
          <input
            type="text"
            value={gameInput}
            onChange={(e) => setGameInput(e.target.value)}
            placeholder="Filtruoti pagal žaidimo pavadinimą..."
            className="w-72 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <button
            type="submit"
            className="rounded-lg border border-primary-300 px-4 py-2 text-sm font-medium text-primary-800 hover:bg-primary-50"
          >
            Filtruoti žaidimą
          </button>
          {gameQuery && (
            <button
              type="button"
              onClick={() => {
                setGameInput("");
                updateParams({ game: "", page: "" });
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Išvalyti
            </button>
          )}
        </form>
        <label className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50">
          <input
            type="checkbox"
            checked={groupByGame}
            onChange={(e) =>
              updateParams({ groupByGame: e.target.checked ? "1" : "", page: "" })
            }
            className="h-4 w-4 rounded border-gray-300 text-primary-700 focus:ring-primary-500"
          />
          Grupuoti pagal žaidimą
        </label>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : result && result.shopListings.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <HeaderCell
                    field="shop_name"
                    label="Parduotuvė"
                    filterCol="shop"
                    cellRef={shopRef}
                  >
                    <CheckboxFilter
                      options={shopOptions}
                      values={shopFilters}
                      onChange={(vals) =>
                        updateParams({ shop: vals.join(","), page: "" })
                      }
                      onClear={() => updateParams({ shop: "", page: "" })}
                    />
                  </HeaderCell>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <button
                      className="inline-flex items-center gap-1 hover:text-gray-700"
                      onClick={() => handleSort("listings_count")}
                      title="Rūšiuoti pagal skelbimų kiekį žaidimui"
                    >
                      Žaidimas
                      {sortBy === "listings_count" && (
                        <span className="text-primary-800">
                          {sortDir === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Variacija
                  </th>
                  <SortOnlyHeader field="listing_name" label="Skelbimas" />
                  <SortOnlyHeader field="price" label="Kaina" />
                  <HeaderCell
                    label="Sandėlis"
                    filterCol="stock"
                    cellRef={stockRef}
                  >
                    <RadioFilter
                      options={STOCK_OPTIONS}
                      value={stockFilter}
                      onChange={(val) =>
                        updateParams({ stock: val, page: "" })
                      }
                      onClear={() => updateParams({ stock: "", page: "" })}
                    />
                  </HeaderCell>
                  <HeaderCell
                    field="price_updated_at"
                    label="Kaina atnaujinta"
                    filterCol="priceUpdated"
                    cellRef={priceUpdatedRef}
                  >
                    <DateRangeFilter
                      preset={priceUpdatedPreset}
                      dateFrom={priceUpdatedFrom}
                      dateTo={priceUpdatedTo}
                      onChange={({ preset, dateFrom: df, dateTo: dt }) =>
                        updateParams({
                          priceUpdatedPreset: preset || "",
                          priceUpdatedFrom: df,
                          priceUpdatedTo: dt,
                          page: "",
                        })
                      }
                      onClear={() =>
                        updateParams({
                          priceUpdatedPreset: "",
                          priceUpdatedFrom: "",
                          priceUpdatedTo: "",
                          page: "",
                        })
                      }
                    />
                  </HeaderCell>
                  <HeaderCell
                    field="stock_checked_at"
                    label="Sandėlis tikrintas"
                    filterCol="stockChecked"
                    cellRef={stockCheckedRef}
                  >
                    <DateRangeFilter
                      preset={stockCheckedPreset}
                      dateFrom={stockCheckedFrom}
                      dateTo={stockCheckedTo}
                      onChange={({ preset, dateFrom: df, dateTo: dt }) =>
                        updateParams({
                          stockCheckedPreset: preset || "",
                          stockCheckedFrom: df,
                          stockCheckedTo: dt,
                          page: "",
                        })
                      }
                      onClear={() =>
                        updateParams({
                          stockCheckedPreset: "",
                          stockCheckedFrom: "",
                          stockCheckedTo: "",
                          page: "",
                        })
                      }
                    />
                  </HeaderCell>
                  <HeaderCell
                    field="created_at"
                    label="Sukurtas"
                    filterCol="created"
                    cellRef={createdRef}
                  >
                    <DateRangeFilter
                      preset={createdPreset}
                      dateFrom={createdFrom}
                      dateTo={createdTo}
                      onChange={({ preset, dateFrom: df, dateTo: dt }) =>
                        updateParams({
                          createdPreset: preset || "",
                          createdFrom: df,
                          createdTo: dt,
                          page: "",
                        })
                      }
                      onClear={() =>
                        updateParams({
                          createdPreset: "",
                          createdFrom: "",
                          createdTo: "",
                          page: "",
                        })
                      }
                    />
                  </HeaderCell>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Nuoroda
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {result.shopListings.map((listing, idx) => {
                  const prev = idx > 0 ? result.shopListings[idx - 1] : null;
                  const showGroupHeader =
                    groupByGame && (!prev || prev.gameId !== listing.gameId);
                  return (
                    <Fragment key={listing.id}>
                      {showGroupHeader && (
                        <tr className="bg-primary-50">
                          <td
                            colSpan={10}
                            className="px-4 py-2 text-sm font-semibold text-primary-900"
                          >
                            {listing.gameId ? (
                              <Link
                                to={`/admin/games/${listing.gameId}`}
                                className="hover:underline"
                              >
                                {listing.gameTitle || `#${listing.gameId}`}
                              </Link>
                            ) : (
                              <span className="text-gray-500">Be žaidimo</span>
                            )}
                            <span className="ml-2 text-xs font-normal text-primary-700">
                              · {listing.gameListingsCount} skelbim.
                            </span>
                          </td>
                        </tr>
                      )}
                  <tr className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                      {listing.shopName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      {listing.gameId ? (
                        <Link
                          to={`/admin/games/${listing.gameId}`}
                          className="text-primary-800 hover:underline"
                        >
                          {listing.gameTitle || `#${listing.gameId}`}
                          <span className="ml-1 text-xs text-gray-500">
                            ({listing.gameListingsCount})
                          </span>
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      {listing.variation ? (
                        <Badge variant="info">{listing.variation}</Badge>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-sm text-gray-600">
                      {listing.listingName || (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                      {listing.price != null ? (
                        `${listing.price.toFixed(2)} €`
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      {stockBadge(listing.inStock)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {formatDate(listing.priceUpdatedAt) || (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {formatDate(listing.stockCheckedAt) || (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {formatDate(listing.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <a
                        href={listing.listingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-800 hover:underline"
                      >
                        Atidaryti ↗
                      </a>
                    </td>
                  </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-6">
            <Pagination
              currentPage={result.currentPage}
              totalPages={result.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white py-12 text-center">
          <p className="text-gray-500">Parduotuvių skelbimų nerasta</p>
        </div>
      )}
    </div>
  );
}
